import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const FairPracticeCode = () => {
  const [activePrinciple, setActivePrinciple] = useState(null);

  // Core principles from the content
  const corePrinciples = [
    {
      id: 1,
      title: "Clear Communication",
      description: "All terms, conditions, and applicable charges are clearly communicated before onboarding",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      )
    },
    {
      id: 2,
      title: "Complete Transparency",
      description: "Customers are provided with complete clarity regarding their obligations and repayment expectations",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      id: 3,
      title: "No Misleading Information",
      description: "No misleading information or false commitments are made at any stage",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      )
    },
    {
      id: 4,
      title: "Data Confidentiality",
      description: "Customer data is handled with strict confidentiality and security measures",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      id: 5,
      title: "Professional Conduct",
      description: "All communication and support are conducted in a professional, respectful, and non-coercive manner",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ];

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* PREMIUM DARK GRADIENT BACKGROUND */}
      <div className="absolute inset-0">
        {/* Base Dark Color */}
        <div className="absolute inset-0 bg-[#0A1620]"></div>

        {/* Deep Blue-Green Accent - Top Right */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>

        {/* Navy Accent - Bottom Left */}
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>

        {/* Very Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* TOP WAVE - Upper Side */}
        <svg
          className="absolute top-0 left-0 w-full h-48 rotate-180"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>

        {/* BOTTOM WAVE - Lower Side */}
        <svg
          className="absolute bottom-0 right-0 w-full h-48"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-20 h-full">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                Customer First • Transparent
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Fair <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">Practice Code</span>
            </h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              SalaryMates is committed to following a customer-first and transparent approach 
              in all its interactions.
            </motion.p>
          </motion.div>

          {/* Core Principles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {corePrinciples.map((principle, index) => (
              <motion.div
                key={principle.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                onHoverStart={() => setActivePrinciple(principle.id)}
                onHoverEnd={() => setActivePrinciple(null)}
                className={`group relative bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border transition-all duration-500 ${
                  activePrinciple === principle.id 
                    ? 'border-[#7BC4E0] shadow-lg shadow-[#7BC4E0]/10' 
                    : 'border-[#2D5F7A]/30 hover:border-[#2D5F7A]'
                }`}
              >
                {/* Hover Effect Background */}
                <div className={`absolute inset-0 bg-gradient-to-r from-[#2D5F7A]/10 to-transparent rounded-[20px] transition-opacity duration-500 ${
                  activePrinciple === principle.id ? 'opacity-100' : 'opacity-0'
                }`}></div>
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-full bg-[#1E3B4A] flex items-center justify-center mb-4 border transition-all duration-500 ${
                    activePrinciple === principle.id ? 'border-[#7BC4E0]' : 'border-[#2D5F7A]/30'
                  }`}>
                    <div className={`transition-colors duration-500 ${
                      activePrinciple === principle.id ? 'text-[#7BC4E0]' : 'text-[#CCE6F5]'
                    }`}>
                      {principle.icon}
                    </div>
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 transition-colors duration-500 ${
                    activePrinciple === principle.id ? 'text-white' : 'text-[#CCE6F5]'
                  }`}>
                    {principle.title}
                  </h3>
                  <p className="text-[#8FAFC7] text-sm leading-relaxed">
                    {principle.description}
                  </p>
                </div>

                {/* Decorative Corner */}
                <div className={`absolute top-0 right-0 w-12 h-12 border-t border-r rounded-tr-[20px] transition-opacity duration-500 ${
                  activePrinciple === principle.id ? 'border-[#7BC4E0] opacity-100' : 'border-transparent opacity-0'
                }`}></div>
              </motion.div>
            ))}
          </div>

          {/* Mission Statement Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[24px] p-8 md:p-10 mb-12 border border-[#2D5F7A]/30 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1E3B4A] flex items-center justify-center flex-shrink-0 border border-[#7BC4E0]/30">
                <svg className="w-6 h-6 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white mb-3">Our Commitment</h2>
                <p className="text-[#CCE6F5] text-lg leading-relaxed">
                  Our aim is to build long-term relationships based on trust, clarity, and ethical practices,
                  ensuring a smooth and reliable experience for every customer.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Grievance Redressal (compliance disclosure, intentionally low-key) */}
          <div className="mb-12 pt-6 border-t border-[#2D5F7A]/20">
            <h2 className="text-sm font-medium text-[#8FAFC7] mb-2">Grievance Redressal</h2>
            <p className="text-xs text-[#8FAFC7]/80 leading-relaxed">
              For complaints regarding our services, contact the Grievance Redressal Officer of
              V.N. Credits Limited &mdash; Sakshi Jinsi,{" "}
              <a href="mailto:grievance@vncredits.in" className="hover:text-[#7BC4E0]">
                grievance@vncredits.in
              </a>
              , +91 93112 27994. Nodal Officer &mdash; Amit Kumar,{" "}
              <a href="mailto:nodal@vncredits.in" className="hover:text-[#7BC4E0]">
                nodal@vncredits.in
              </a>
              , +91 93112 27994. We aim to resolve all complaints within 30 days. If your complaint
              is not resolved within 30 days, or you are not satisfied with the resolution, you may
              escalate it to the Reserve Bank of India under the Reserve Bank &ndash; Integrated
              Ombudsman Scheme, 2021 at{" "}
              <a
                href="https://cms.rbi.org.in"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#7BC4E0] underline"
              >
                cms.rbi.org.in
              </a>
              , or by writing to the Centralised Receipt and Processing Centre, Reserve Bank of
              India, Central Vista, Sector 17, Chandigarh &ndash; 160017.
            </p>
          </div>



        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">Transparency</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">Trust</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">Ethics</span>
      </div>
    </section>
  );
};

export default FairPracticeCode;